import React, { useState, useEffect, useRef } from 'react';
import { Sparkles, Scale, Gem, ArrowRight, TrendingUp } from 'lucide-react';
import { useToast } from '../../context/ToastContext';
import './DiamondPricePage.css';
import { 
  TextField, 
  Button, 
  Select, 
  MenuItem, 
  FormControl, 
  InputLabel, 
  Typography,
  Box,
  Paper,
  Divider,
  FormHelperText,
  InputAdornment,
  CircularProgress,
  Chip
} from '@mui/material';
import { calculateDiamondPrice, getDiamondMarketTrends } from '../../services/DiamondPriceService';
import styled from 'styled-components';
import { Link, useLocation } from 'react-router-dom';
import WhatsAppButton from '../common/WhatsAppButton';
import useWhatsAppContact from '../../hooks/useWhatsAppContact';
import SEOMetaTags from '../common/SEOMetaTags';

const PriceDisclaimer = styled.div`
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 15px;
  margin: 20px 0;
  font-size: 0.9em;
  color: #aaa;
  
  strong {
    color: #fff;
  }
`;

const MarketTrends = styled.div`
  margin-top: 20px;
  padding: 15px;
  background: linear-gradient(45deg, rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.1));
  border-radius: 8px;
  
  h4 {
    color: #4a90e2;
    margin-bottom: 10px;
  }
  
  .trend-item {
    display: flex;
    justify-content: space-between;
    margin: 5px 0;
    
    .trend-label {
      color: #ddd;
    }
    
    .trend-value {
      color: #4a90e2;
      font-weight: bold;
    }
    
    &.positive {
      .trend-value {
        color: #4caf50;
      }
    }
    
    &.negative {
      .trend-value {
        color: #f44336;
      }
    }
  }
`;

const DiamondPricePage = () => {
  const { showSuccessToast, showErrorToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [price, setPrice] = useState(null);
  const [marketTrends, setMarketTrends] = useState(null);
  const [loadingTrends, setLoadingTrends] = useState(false);
  const resultContainerRef = useRef(null);
  const { createDiamondProduct } = useWhatsAppContact();
  const location = useLocation();
  const [formData, setFormData] = useState({
    carat: '',
    clarity: '',
    color: '',
    cut: ''
  });

  // Diamond quality options
  const cutOptions = ['Fair', 'Good', 'Very Good', 'Premium', 'Ideal'];
  const colorOptions = ['D', 'E', 'F', 'G', 'H', 'I', 'J'];
  const clarityOptions = ['FL', 'IF', 'VVS1', 'VVS2', 'VS1', 'VS2', 'SI1', 'SI2', 'I1'];

  // Fetch market trends on component mount
  useEffect(() => {
    const fetchMarketTrends = async () => {
      try {
        const trends = await getDiamondMarketTrends();
        setMarketTrends(trends);
      } catch (error) {
        console.error('Error fetching market trends:', error);
      }
    };
    
    fetchMarketTrends();
  }, []);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  // Calculate diamond price based on inputs using our service
  const calculatePrice = async () => {
    setLoading(true);
    
    try {
      const calculatedPrice = await calculateDiamondPrice(formData);
      setPrice(calculatedPrice);
      showSuccessToast('Price calculated successfully!');
      
      // Scroll to result container after price is calculated
      setTimeout(() => {
        if (resultContainerRef.current) {
          resultContainerRef.current.scrollIntoView({ 
            behavior: 'smooth',
            block: 'start'
          });
        }
      }, 100);
    } catch (error) {
      console.error('Error calculating price:', error);
      showErrorToast('Failed to calculate price. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    calculatePrice();
  };

  return (
    <div className="diamond-price-page">
      <SEOMetaTags 
        title="Diamond Price Estimator | Calculate Diamond Values | NM Diamond"
        description="Use our free diamond price calculator to estimate the value of your diamond based on the 4Cs: carat, cut, color, and clarity. Get accurate diamond pricing from NM Diamond experts."
        keywords="diamond price calculator, diamond value estimator, diamond 4Cs, calculate diamond price, diamond pricing tool"
        canonicalUrl="https://www.nmdiamond.in/diamonds"
        alternateUrl={location.pathname === "/diamonds" ? "https://www.nmdiamond.in/diamond-evaluator" : null}
        isRedirectTarget={location.pathname === "/diamonds" && location.key.includes("replace")}
      />
      
      <Box className="diamond-header">
        <Typography variant="h3" className="section-title">
          Diamond Price Estimator
        </Typography>
        <Typography variant="subtitle1" className="section-subtitle">
          Discover the value of your diamond based on the 4Cs
        </Typography>
        {marketTrends && (
          <Chip 
            icon={<TrendingUp size={16} />} 
            label={`Market trend: ${marketTrends.overall.change > 0 ? '+' : ''}${marketTrends.overall.change}% in the last ${marketTrends.overall.period}`}
            color="primary"
            variant="outlined"
            className="market-trend-chip"
          />
        )}
      </Box>

      <Box className="diamond-content">
        <Paper className="diamond-form-container" elevation={3}>
          <Box component="form" className="diamond-form" onSubmit={handleSubmit}>
            <Box className="form-row">
              <FormControl className="form-field" fullWidth>
                <InputLabel htmlFor="carat" shrink>
                  Carat Weight
                </InputLabel>
                <TextField
                  id="carat"
                  name="carat"
                  type="number"
                  value={formData.carat}
                  onChange={handleInputChange}
                  required
                  fullWidth
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Scale size={20} className="form-icon" />
                      </InputAdornment>
                    ),
                  }}
                  inputProps={{
                    min: "0.1",
                    max: "10",
                    step: "0.01"
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
                <FormHelperText>
                  Weight of the diamond (0.1 to 10 carats)
                </FormHelperText>
              </FormControl>

              <FormControl className="form-field" fullWidth>
                <InputLabel id="cut-label" shrink>Cut</InputLabel>
                <Select
                  labelId="cut-label"
                  id="cut"
                  name="cut"
                  value={formData.cut}
                  onChange={handleInputChange}
                  required
                  displayEmpty
                  startAdornment={
                    <InputAdornment position="start">
                      <Gem size={20} className="form-icon" />
                    </InputAdornment>
                  }
                  label="Cut"
                  className="select-with-icon"
                >
                  {cutOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  Quality of the diamond's cut
                </FormHelperText>
              </FormControl>
            </Box>

            <Box className="form-row">
              <FormControl className="form-field" fullWidth>
                <InputLabel id="color-label" shrink>Color</InputLabel>
                <Select
                  labelId="color-label"
                  id="color"
                  name="color"
                  value={formData.color}
                  onChange={handleInputChange}
                  required
                  displayEmpty
                  startAdornment={
                    <InputAdornment position="start">
                      <Sparkles size={20} className="form-icon" />
                    </InputAdornment>
                  }
                  label="Color"
                  className="select-with-icon"
                >
                  {colorOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  Color grade (D is colorless, J has slight color)
                </FormHelperText>
              </FormControl>

              <FormControl className="form-field" fullWidth>
                <InputLabel id="clarity-label" shrink>Clarity</InputLabel>
                <Select
                  labelId="clarity-label"
                  id="clarity"
                  name="clarity"
                  value={formData.clarity}
                  onChange={handleInputChange}
                  required
                  displayEmpty
                  startAdornment={
                    <InputAdornment position="start">
                      <Sparkles size={20} className="form-icon" />
                    </InputAdornment>
                  }
                  label="Clarity"
                  className="select-with-icon"
                >
                  {clarityOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  Clarity grade (IF is flawless, I1 has inclusions)
                </FormHelperText>
              </FormControl>
            </Box>

            <Box className="form-actions">
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={loading}
                className="calculate-button"
                endIcon={loading ? <CircularProgress size={20} color="inherit" /> : <ArrowRight />}
              >
                {loading ? 'Calculating...' : 'Calculate Price'}
              </Button>
            </Box>
          </Box>
        </Paper>

        <Paper className="diamond-result-container" elevation={3} ref={resultContainerRef}>
          {price !== null ? (
            <>
              <Box className="price-result">
                <Typography variant="h6" component="h3">Estimated Price</Typography>
                <Box className="price-value">
                  <Typography variant="h4" component="span">₹{price.toLocaleString()}</Typography>
                </Box>
                <Typography variant="body2" className="price-note">
                  This is an estimated price based on current market rates.
                  Contact us for a personalized quote.
                </Typography>
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
                  <WhatsAppButton 
                    product={createDiamondProduct({
                      carat: formData.carat,
                      clarity: formData.clarity,
                      color: formData.color,
                      cut: formData.cut,
                      price: price,
                      image: '/media/diamond-price-result.jpg'
                    })}
                    buttonText="Contact for Purchase"
                    showShareOptions={true}
                    buttonProps={{
                      sx: { 
                        borderRadius: '25px', 
                        padding: '10px 20px',
                        backgroundColor: '#25D366',
                        '&:hover': {
                          backgroundColor: '#128C7E'
                        }
                      }
                    }}
                  />
                  <Button 
                    variant="outlined" 
                    component={Link}
                    to="/diamond-matchmaker"
                    sx={{ 
                      borderRadius: '25px', 
                      padding: '10px 20px',
                      borderColor: '#3498db',
                      color: '#3498db',
                      '&:hover': {
                        borderColor: '#8e44ad',
                        color: '#8e44ad',
                        backgroundColor: 'rgba(142, 68, 173, 0.05)'
                      }
                    }}
                  >
                    Find Your Perfect Diamond
                  </Button>
                </Box>
              </Box>
              
              <Divider sx={{ my: 3, borderColor: 'rgba(255, 255, 255, 0.1)' }} />
            </>
          ) : null}
          
          <Box className="diamond-info">
            <Typography variant="h5" component="h2" className="info-title">
              About Diamond Pricing
            </Typography>
            <Typography variant="body1" paragraph className="info-text">
              Diamond prices are primarily determined by the 4Cs: Carat, Cut, Color, and Clarity.
            </Typography>
            <Box className="diamond-factors">
              <Typography component="ul">
                <li>
                  <strong>Carat:</strong> The weight of the diamond. Larger diamonds are rarer and more valuable.
                </li>
                <li>
                  <strong>Cut:</strong> How well the diamond is cut, affecting its brilliance and sparkle.
                </li>
                <li>
                  <strong>Color:</strong> The absence of color, with D (colorless) being the highest grade.
                </li>
                <li>
                  <strong>Clarity:</strong> The absence of inclusions and blemishes, with IF (Internally Flawless) being the highest grade.
                </li>
              </Typography>
            </Box>
            <PriceDisclaimer>
              <strong>Price Estimate Notice:</strong> Our diamond pricing model uses current market data, 
              GIA standards, and industry trends to provide accurate estimates. Actual prices may vary 
              based on market conditions, specific diamond characteristics, and retailer margins. 
              For exact pricing, please consult with certified diamond dealers.
            </PriceDisclaimer>
            {marketTrends && (
              <MarketTrends>
                <h4>Current Market Trends</h4>
                <div className={`trend-item ${marketTrends.overall.change >= 0 ? 'positive' : 'negative'}`}>
                  <span className="trend-label">Overall Market</span>
                  <span className="trend-value">
                    {marketTrends.overall.change >= 0 ? '+' : ''}{marketTrends.overall.change}% 
                    ({marketTrends.overall.period})
                  </span>
                </div>
                <div className="trend-item">
                  <span className="trend-label">Premium Diamonds</span>
                  <span className="trend-value">+{marketTrends.byCategory.premium}%</span>
                </div>
                <div className="trend-item">
                  <span className="trend-label">Mid-Range</span>
                  <span className="trend-value">+{marketTrends.byCategory.midRange}%</span>
                </div>
                <div className="trend-item">
                  <span className="trend-label">Budget Range</span>
                  <span className="trend-value">+{marketTrends.byCategory.budget}%</span>
                </div>
                <div className="trend-item" style={{ marginTop: '10px', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '10px' }}>
                  <span className="trend-label">Market Forecast</span>
                  <span className="trend-value" style={{ fontSize: '0.9em', maxWidth: '60%', textAlign: 'right' }}>
                    {marketTrends.forecast}
                  </span>
                </div>
                <div style={{ fontSize: '0.8em', color: '#666', marginTop: '10px', textAlign: 'right' }}>
                  Last updated: {new Date(marketTrends.lastUpdated).toLocaleString()}
                </div>
              </MarketTrends>
            )}
            <Typography variant="body1" paragraph className="info-text">
              This estimator provides an approximate value based on current market trends.
              For an accurate appraisal, please contact our diamond specialists.
            </Typography>
          </Box>
        </Paper>
      </Box>
    </div>
  );
};

export default DiamondPricePage; 